import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Drawer,
  Typography,
  Chip,
  Divider,
  IconButton,
  Stack,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Claim_ClaimsDbModel } from "@safelease/service-utilities";
import TenantActionsAccordion from "./TenantActionsAccordion";
import { formatCurrency, formatDate, getStatusDisplayText, getStatusChipColors } from "../utils/claimStatusUtils";
import { StatusHelpTooltip, getStatusHelpText } from "./StatusHelpTooltip";
import { ClaimDetailsFooter } from "./ClaimDetailsFooter";
import { mixpanelEventHandler } from "../../utilities/reactMixpanelHandler";
import { CLAIMS_MIXPANEL_EVENTS, ACCORDION_TYPES, AccordionType } from "./claimsMixpanelEvents";

interface ClaimDetailsDrawerProps {
  claim: Claim_ClaimsDbModel | null;
  open: boolean;
  onClose: () => void;
  drawerOpenedVia?: "row_click" | "url";
}

/**
 * Modern MUI-based claim details drawer that works directly with REST API data
 */
function ClaimDetailsDrawer({ claim, open, onClose, drawerOpenedVia = "row_click" }: ClaimDetailsDrawerProps) {
  const [copied, setCopied] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<AccordionType>>(new Set());
  const copyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedOpen = useRef(false);

  // Cleanup timeout on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Track drawer opened event
  useEffect(() => {
    if (open && claim && !hasTrackedOpen.current) {
      const locationId = claim.unit?.safeleaseLocationDetail?.locationId || null;
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.opened, {
        claim_id: claim.id,
        claim_status: claim.status,
        locationId: locationId,
        drawer_opened_via: drawerOpenedVia,
      });
      hasTrackedOpen.current = true;
    }
    // Reset tracking flag when drawer closes
    if (!open) {
      hasTrackedOpen.current = false;
    }
  }, [open, claim, drawerOpenedVia]);

  if (!claim) return null;

  // Handle claim ID copy
  const handleCopyClaimId = () => {
    navigator.clipboard.writeText(claim.id);
    setCopied(true);

    // Track claim ID copy from drawer
    const locationId = claim.unit?.safeleaseLocationDetail?.locationId || null;
    mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.claimIdCopied, {
      claim_id: claim.id,
      claim_status: claim.status,
      locationId: locationId,
    });

    // Clear any existing timeout
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }

    // Set new timeout and store reference
    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
      copyTimeoutRef.current = null;
    }, 2000);
  };

  // Handle drawer close with tracking
  const handleClose = () => {
    if (claim) {
      const locationId = claim.unit?.safeleaseLocationDetail?.locationId || null;
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.closed, {
        claim_id: claim.id,
        claim_status: claim.status,
        locationId: locationId,
      });
    }
    onClose();
  };

  // Handle accordion expansion/collapse tracking
  const handleAccordionChange = (accordionType: AccordionType, isExpanded: boolean) => {
    const locationId = claim.unit?.safeleaseLocationDetail?.locationId || null;
    const newExpandedAccordions = new Set(expandedAccordions);

    if (isExpanded) {
      newExpandedAccordions.add(accordionType);
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.accordionExpanded, {
        claim_id: claim.id,
        claim_status: claim.status,
        accordion_type: accordionType,
        locationId: locationId,
      });
    } else {
      newExpandedAccordions.delete(accordionType);
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.accordionCollapsed, {
        claim_id: claim.id,
        claim_status: claim.status,
        accordion_type: accordionType,
        locationId: locationId,
      });
    }
    setExpandedAccordions(newExpandedAccordions);
  };

  // Helper function for currency with 2 decimals (details view shows cents)
  const formatCurrencyWithDecimals = (amount: number | null) => formatCurrency(amount, 2);

  // Calculate claim amount from line items
  const claimAmount =
    claim.lineItems && claim.lineItems.length > 0
      ? claim.lineItems.reduce((sum: number, item: any) => sum + (item.requestedAmount || 0), 0) / 100
      : 0;

  // Get coverage amount
  const coverageAmount = claim.unit?.selectedPolicyMatchData?.coverage ? claim.unit.selectedPolicyMatchData.coverage / 100 : null;

  // Get tenant name
  const tenantName = claim.unit?.cognitoUserDetail
    ? `${claim.unit.cognitoUserDetail.userFirstName || ""} ${claim.unit.cognitoUserDetail.userLastName || ""}`.trim()
    : "N/A";

  // Get tenant email
  const tenantEmail = claim.unit?.cognitoUserDetail?.userEmail || "N/A";

  // Get location info
  const locationName = claim.unit?.safeleaseLocationDetail?.locationName || "N/A";
  const fullAddress = claim.unit?.safeleaseLocationDetail
    ? `${claim.unit.safeleaseLocationDetail.address || ""}, ${claim.unit.safeleaseLocationDetail.city || ""}, ${claim.unit.safeleaseLocationDetail.state || ""} ${claim.unit.safeleaseLocationDetail.postalCode || ""}`.trim()
    : "N/A";

  // Get contextual help text for the alert banner (now imported from StatusHelpTooltip)
  const statusHelpText = getStatusHelpText(claim.status);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: 650 },
          padding: 0,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "primary.main",
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Unit title */}
            <Typography variant="h4" sx={{ fontWeight: 700, color: "white", mb: 2 }}>
              Unit {claim.unit?.name || "N/A"}
            </Typography>

            {/* Status chip with contextual help tooltip */}
            <Box sx={{ mb: 2 }}>
              <StatusHelpTooltip status={claim.status} placement="bottom" claimId={claim.id}>
                <Chip
                  label={getStatusDisplayText(claim.status)}
                  size="medium"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    height: "32px",
                    backgroundColor: getStatusChipColors(claim.status).backgroundColor,
                    color: getStatusChipColors(claim.status).color,
                    "& .MuiChip-label": {
                      px: 2,
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </StatusHelpTooltip>
            </Box>

            {/* Subtitle: ClaimType | Claim # | Address */}
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.9)",
                lineHeight: 1.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {claim.claimType?.name || "Unknown Claim Type"} | Claim #{claim.id.split("-")[0]} | {fullAddress}
            </Typography>
          </Box>

          {/* Top-right buttons */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              onClick={handleCopyClaimId}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                transition: "background-color 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              {copied ? (
                <CheckIcon fontSize="small" sx={{ color: "white" }} />
              ) : (
                <ContentCopyIcon fontSize="small" sx={{ color: "white" }} />
              )}
              <Typography variant="body2" sx={{ color: "white", fontSize: "0.875rem" }}>
                {copied ? "Copied!" : "Copy claim ID"}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Alert Banner - Contextual help based on status */}
      {statusHelpText && (
        <Box
          sx={{
            mx: 2,
            mt: 2,
            p: 2,
            backgroundColor: "rgba(222, 113, 1, 0.08)",
            border: "1px solid",
            borderColor: "#DE7101",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#DE7101",
              lineHeight: 1.6,
            }}
          >
            {statusHelpText}
          </Typography>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        {/* Tenant Actions - Only show pending actions */}
        {claim.tenantActions && claim.tenantActions.length > 0 && (
          <TenantActionsAccordion
            tenantActions={claim.tenantActions}
            onAccordionChange={(isExpanded) => handleAccordionChange(ACCORDION_TYPES.TENANT_ACTIONS, isExpanded)}
          />
        )}

        {/* Claim Details */}
        <Accordion
          sx={{
            mb: 1,
            borderRadius: "8px !important",
            "&:before": { display: "none" },
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          }}
          onChange={(_, isExpanded) => handleAccordionChange(ACCORDION_TYPES.CLAIM_DETAILS, isExpanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: "grey.50",
              borderRadius: "8px",
              "&.Mui-expanded": { minHeight: 48 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
              Claim Details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 2 }}>
            <Stack spacing={2.5} divider={<Divider />}>
              <DetailRow label="Tenant" value={tenantName} claim={claim} />
              <DetailRow label="Email" value={tenantEmail} email claim={claim} />
              <DetailRow label="Location" value={locationName} claim={claim} />
              <DetailRow label="Facility Address" value={fullAddress} claim={claim} />
              <DetailRow label="Coverage Amount" value={formatCurrencyWithDecimals(coverageAmount)} claim={claim} />
              <DetailRow label="Claim Amount" value={formatCurrencyWithDecimals(claimAmount)} claim={claim} />
              <DetailRow
                label="Adjuster Value"
                value={formatCurrencyWithDecimals(claim.adjusterValue ? claim.adjusterValue / 100 : null)}
                claim={claim}
              />
              <DetailRow label="Cause" value={claim.claimType?.name || "N/A"} claim={claim} />
              <DetailRow label="Loss Date" value={formatDate(claim.lossDate)} claim={claim} />
              <DetailRow label="Submitted" value={formatDate(claim.submittedOn)} claim={claim} />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Description */}
        {claim.lossDescription && (
          <Accordion
            sx={{
              mb: 1,
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            }}
            onChange={(_, isExpanded) => handleAccordionChange(ACCORDION_TYPES.LOSS_DESCRIPTION, isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "grey.50",
                borderRadius: "8px",
                "&.Mui-expanded": { minHeight: 48 },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
                Loss Description
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.7,
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                {claim.lossDescription}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Line Items */}
        {claim.lineItems && claim.lineItems.length > 0 && (
          <Accordion
            sx={{
              mb: 1,
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            }}
            onChange={(_, isExpanded) => handleAccordionChange(ACCORDION_TYPES.LINE_ITEMS, isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "grey.50",
                borderRadius: "8px",
                "&.Mui-expanded": { minHeight: 48 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
                  Line Items
                </Typography>
                <Chip label={claim.lineItems.length} size="small" color="default" sx={{ fontWeight: 600 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Stack spacing={2} divider={<Divider />}>
                {claim.lineItems.map((item: any, index: number) => (
                  <Box key={index}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {item.name || `Item ${index + 1}`}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Chip
                        label={`Requested: ${formatCurrencyWithDecimals(item.requestedAmount ? item.requestedAmount / 100 : 0)}`}
                        size="small"
                        sx={{ backgroundColor: "primary.50", color: "primary.main", fontWeight: 500 }}
                      />
                      {item.adjusterValue && (
                        <Chip
                          label={`Adjuster: ${formatCurrencyWithDecimals(item.adjusterValue / 100)}`}
                          size="small"
                          sx={{ backgroundColor: "success.50", color: "success.main", fontWeight: 500 }}
                        />
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Documents */}
        {(claim.typeSpecificAttachments?.length > 0 || claim.claimDocuments?.length > 0 || claim.additionalClaimDocuments?.length > 0) && (
          <Accordion
            sx={{
              mb: 1,
              borderRadius: "8px !important",
              "&:before": { display: "none" },
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            }}
            onChange={(_, isExpanded) => handleAccordionChange(ACCORDION_TYPES.DOCUMENTS, isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: "grey.50",
                borderRadius: "8px",
                "&.Mui-expanded": { minHeight: 48 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
                  Documents & Attachments
                </Typography>
                <Chip
                  label={
                    (claim.typeSpecificAttachments?.length || 0) +
                    (claim.claimDocuments?.length || 0) +
                    (claim.additionalClaimDocuments?.length || 0)
                  }
                  size="small"
                  color="default"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              <Stack spacing={1.5}>
                {claim.typeSpecificAttachments?.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={claim.typeSpecificAttachments.length} size="small" color="primary" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" color="text.secondary">
                      Type-specific attachments
                    </Typography>
                  </Box>
                )}
                {claim.claimDocuments?.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={claim.claimDocuments.length} size="small" color="default" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" color="text.secondary">
                      Claim documents
                    </Typography>
                  </Box>
                )}
                {claim.additionalClaimDocuments?.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip label={claim.additionalClaimDocuments.length} size="small" color="default" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" color="text.secondary">
                      Additional documents
                    </Typography>
                  </Box>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* Fixed Footer - Contact Information */}
      <ClaimDetailsFooter claim={claim} />
    </Drawer>
  );
}

// Helper component for detail rows
function DetailRow({ label, value, email, claim }: { label: string; value: string; email?: boolean; claim?: Claim_ClaimsDbModel | null }) {
  const handleEmailClick = () => {
    if (claim && email && value !== "N/A") {
      const locationId = claim.unit?.safeleaseLocationDetail?.locationId || null;
      mixpanelEventHandler(CLAIMS_MIXPANEL_EVENTS.drawer.emailClicked, {
        claim_id: claim.id,
        claim_status: claim.status,
        locationId: locationId,
        tenant_email: value,
      });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500, color: "text.primary" }}>
        {email && value !== "N/A" ? (
          <a
            href={`mailto:${value}`}
            onClick={handleEmailClick}
            style={{
              color: "#1976d2",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {value}
          </a>
        ) : (
          value
        )}
      </Typography>
    </Box>
  );
}

export default ClaimDetailsDrawer;
